import { FC, useMemo, useState } from "react";
import { IOrder, IRefundItem, IRefundRequest } from "../../../model/IOrder";
import Button from "../../form/Button";
import Input, { TInputEvent } from "../../form/Input";

const TYPES = [
  { title: 'Refund', value: 'Refund' },
  { title: 'Chargeback', value: 'Chargeback' },
];

/** The form's own shape: amounts are text while being typed. */
type TDraft = {
  type: string,
  partPrice: string,
  freight: string,
  automotiveCare: string,
  taxes: string,
  supplierKey: string,
  comments: string,
};

const asDraft = (refund?: IRefundItem | null, supplierKey = ''): TDraft => ({
  type: refund?.type || 'Refund',
  // -- A null amount shows as empty, not "0". The two mean different things
  // -- on a refund: nothing recorded, versus recorded as nothing.
  partPrice: refund?.partPrice == null ? '' : String(refund.partPrice),
  freight: refund?.freight == null ? '' : String(refund.freight),
  automotiveCare: refund?.automotiveCare == null ? '' : String(refund.automotiveCare),
  taxes: refund?.taxes == null ? '' : String(refund.taxes),
  supplierKey: refund?.supplierKey || supplierKey,
  comments: refund?.comments || '',
});

/** Blank stays null; anything else has to be a number. */
const parseAmount = (raw: string): number | null | undefined => {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const value = Number(trimmed);
  return Number.isFinite(value) ? value : undefined;
};

const AMOUNTS: { key: keyof TDraft, label: string }[] = [
  { key: 'partPrice', label: 'Part' },
  { key: 'freight', label: 'Freight' },
  { key: 'automotiveCare', label: 'Shipping Protection' },
  { key: 'taxes', label: 'Tax' },
];

/**
 * The suppliers this order was filled by.
 *
 * Distinct, in the order the items appear, blanks dropped. Whatever the refund
 * already carries is kept in the list even when no item has that key any more
 * - otherwise opening an older refund would quietly swap its supplier for the
 * first one in the dropdown.
 */
const supplierOptions = (order: IOrder, current?: string | null) => {
  const keys: string[] = [];
  (order.orderItems || []).forEach(({ supplierExternalKey }) => {
    const key = (supplierExternalKey || '').trim();
    if (key && !keys.includes(key)) keys.push(key);
  });
  if (current && !keys.includes(current)) keys.push(current);
  return keys;
};

const RefundForm: FC<{
  order: IOrder,
  refund?: IRefundItem | null,
  isVendorRefund: boolean,
  loading?: boolean,
  onSubmit: (request: IRefundRequest) => void,
}> = ({ order, refund, isVendorRefund, loading, onSubmit }) => {
  const suppliers = useMemo(
    () => supplierOptions(order, refund?.supplierKey),
    [order, refund?.supplierKey],
  );

  // -- One supplier on the order means there is nothing to choose; pick it.
  const [draft, setDraft] = useState<TDraft>(
    () => asDraft(refund, suppliers.length === 1 ? suppliers[0] : ''),
  );
  const [errors, setErrors] = useState<Partial<Record<keyof TDraft, string>>>({});

  // Input's handler covers input, select and textarea - the Type field is a
  // select and Comments is a textarea, so the narrow event type will not do.
  const set = (key: keyof TDraft) => (e: TInputEvent) =>
    setDraft((prev) => ({ ...prev, [key]: e?.target?.value ?? '' }));

  const handleSubmit = () => {
    const next: Partial<Record<keyof TDraft, string>> = {};
    const amounts: Record<string, number | null> = {};

    AMOUNTS.forEach(({ key, label }) => {
      const value = parseAmount(draft[key]);
      if (value === undefined) next[key] = `${label} must be a number`;
      else amounts[key] = value;
    });

    if (!draft.type) next.type = 'Type is required';
    if (!draft.supplierKey.trim()) next.supplierKey = 'Vendor code is required';

    // -- A refund of nothing at all is almost always a slip. The server and
    // -- QBO both tolerate it - QBO just skips the row - so this is a guard
    // -- here rather than a rule there.
    const hasAmount = AMOUNTS.some(({ key }) => {
      const value = amounts[key];
      return value !== null && value !== undefined && value !== 0;
    });
    if (!Object.keys(next).length && !hasAmount) next.partPrice = 'Enter at least one amount';

    setErrors(next);
    if (Object.keys(next).length) return;

    onSubmit({
      type: draft.type,
      isVendorRefund,
      partPrice: amounts.partPrice ?? null,
      freight: amounts.freight ?? null,
      automotiveCare: amounts.automotiveCare ?? null,
      taxes: amounts.taxes ?? null,
      supplierKey: draft.supplierKey.trim(),
      comments: draft.comments.trim() || null,
    });
  };

  const posted = !!refund?.qboJournalEntryId;

  return (
    <div className="w-full grid grid-cols-12 gap-x-5 gap-y-5">
      {posted && (
        <div className="col-span-12 rounded border-[1px] border-amber-300 bg-amber-50 px-3 py-2 text-sm">
          <b>Already posted to QuickBooks</b> as journal entry {refund?.qboJournalEntryId}.
          Saving changes this record here only — the entry is <b>not</b> re-sent, because that
          would post the amount a second time. Amend it in QuickBooks to match.
        </div>
      )}

      <Input
        label="Type"
        name="type"
        value={draft.type}
        options={TYPES}
        handle={set('type')}
        error={errors.type}
        disabled={loading}
        className="col-span-6"
      />
      {/*
        The suppliers on this order. If the order has none recorded - every
        item's key is blank - the field falls back to free text rather than an
        empty dropdown, so a refund can still be entered.
      */}
      <Input
        label="Vendor Code"
        name="supplierKey"
        placeholder={suppliers.length ? undefined : 'No supplier on this order - type one'}
        value={draft.supplierKey}
        {...(suppliers.length
          ? { options: suppliers.map((key) => ({ title: key, value: key })) }
          : {})}
        handle={set('supplierKey')}
        error={errors.supplierKey}
        isError={!!errors.supplierKey}
        disabled={loading}
        className="col-span-6"
      />

      {AMOUNTS.map(({ key, label }) => (
        <Input
          key={key}
          label={label}
          name={key}
          placeholder="0.00"
          value={draft[key]}
          handle={set(key)}
          error={errors[key]}
          disabled={loading}
          className="col-span-6 xl:col-span-3"
        />
      ))}

      <Input
        label="Comments"
        name="comments"
        type="textarea"
        value={draft.comments}
        handle={set('comments')}
        disabled={loading}
        className="col-span-12"
      />

      <p className="col-span-12 text-text_pale text-sm">
        Saving sends this order's refunds to QuickBooks. Records already posted are left alone.
      </p>

      <div className="col-span-12 flex justify-end">
        <Button isNotFullWidth type="submit" disabled={loading} onClick={handleSubmit}>
          {loading ? 'Saving…' : (refund ? 'Save Refund' : 'Add Refund')}
        </Button>
      </div>
    </div>
  );
};

export default RefundForm;
